/**
 * PUT a file to S3 using XMLHttpRequest.
 *
 * The shared `http` client was migrated from axios to native `fetch` (#20385).
 * iOS/iPadOS WebKit has a long-standing bug where a cross-origin `fetch()` PUT
 * of a `File`/`Blob` body rejects with the opaque "Load failed" — which is
 * exactly how painter photo uploads broke on iPad (#21104). axios used
 * XMLHttpRequest under the hood in the browser, which is why uploads worked
 * before the migration, so we PUT via XHR here to sidestep the WebKit bug.
 *
 * Browser-only: callers must guard with `typeof XMLHttpRequest !== 'undefined'`
 * and fall back to the fetch client on the server (Node has no XHR).
 */
export default function putFileViaXhr({
  url,
  file,
  headers,
  timeout
}: {
  url: string
  file: File
  headers: Record<string, string>
  timeout: number
}): Promise<{ status: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.timeout = timeout

    for (const [key, value] of Object.entries(headers)) {
      if (value !== undefined && value !== null) xhr.setRequestHeader(key, String(value))
    }

    // Include a short slice of the S3 error body (e.g. an <Error> XML payload)
    // so a residual failure reports something actionable instead of "Load failed".
    const detail = () => {
      const body = (xhr.responseText || '').trim().slice(0, 200)
      return body ? `: ${body}` : ''
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ status: xhr.status })
      } else {
        reject(new Error(`S3 upload failed with status ${xhr.status}${detail()}`))
      }
    }
    xhr.onerror = () => reject(new Error('S3 upload network error'))
    xhr.ontimeout = () => {
      // Mirror the fetch client's timeout mapping (http.ts) so downstream
      // handlers treat XHR and fetch timeouts identically.
      const err = new Error(`S3 upload timed out after ${timeout}ms`) as Error & { code?: string }
      err.code = 'ECONNABORTED'
      reject(err)
    }
    xhr.onabort = () => reject(new Error('S3 upload aborted'))

    xhr.send(file)
  })
}
